import { useRef, useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Carousel } from 'primereact/carousel';
import { useTranslation } from 'react-i18next';
import bgImage from '../../assets/images/new_bgs.png';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import InputText from '../../components/InputText';
import { Checkbox } from 'primereact/checkbox';
import DistanceSalesModal from '../../components/Contracts/DistanceSalesModal';
import axiosInstance from '../../utils/axiosInstance';
import { useLoading } from '../../components/Loading/LoadingProvider';
import { useAuth } from '../../utils/AuthProvider';

const Pricing = () => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
    const [showUserInfoDialog, setShowUserInfoDialog] = useState(false);
    const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [distanceSalesAgreement, setDistanceSalesAgreement] = useState(false);
    const [distanceSalesModalVisible, setDistanceSalesModalVisible] = useState(false);

    const { setLoading } = useLoading();
    const toast = useRef(null);
    const { t } = useTranslation();
    const { user } = useAuth();

    const [userInfo, setUserInfo] = useState({
        name: '',
        phoneNumber: '',
        identity: '',
        description: '',
        paymentAmount: 0,
        paymentMethod: '',
        planMonths: 0
    });

    // User verisi geldiğinde userInfo'yu güncelle
    useEffect(() => {
        if (user) {
            setUserInfo(prev => ({
                ...prev,
                name: user.name && user.surname ? `${user.name} ${user.surname}` : user.name || '',
                phoneNumber: user.phoneNumber1 || '',
                identity: user.identityNumber || '',
                // description alanı boş kalır, kullanıcı isterse doldurur
            }));
        }
    }, [user]);

    // Dinamik fiyatlandırma parametreleri - Sıralama: 12→6→3→1
    const pricingConfig = {
        basePrice: 2500,
        discounts: {
            3: 5,
            6: 10,
            12: 20,
        },
        transferDiscount: 5,
        plans: [
            {
                id: 4,
                months: 12,
                label: t('annual'),
                isPopular: true,
                savings: "En Çok Tasarruf",
                badge: "💎 En Avantajlı"
            },
            {
                id: 3,
                months: 6,
                label: t('sixMonth'),
                savings: "Orta Vadeli",
                badge: "⭐ Popüler"
            },
            {
                id: 2,
                months: 3,
                label: t('threeMonth'),
                savings: "Kısa Vadeli"
            },
            {
                id: 1,
                months: 1,
                label: t('monthly'),
                savings: "Aylık"
            },
        ]
    };

    // Dinamik banka hesapları
    const bankAccounts = [
        { bank: "Ziraat Bankası", iban: "TR30 0001 0012 2953 4294 6650 05", name: "Seyfullah Çalışkan" },
        { bank: "Garanti Bankası", iban: "TR25 0006 2001 0710 0006 2953 57", name: "Seyfullah Çalışkan" },
        { bank: "Akbank", iban: "TR06 0004 6000 7788 8000 2902 86", name: "Seyfullah Çalışkan" },
    ];

    // Kopyalama fonksiyonu
    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.current.show({
                severity: 'success',
                summary: 'Kopyalandı!',
                detail: `${type} panoya kopyalandı`,
                life: 2000,
            });
        } catch (err) {
            // Fallback method for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);

            toast.current.show({
                severity: 'success',
                summary: 'Kopyalandı!',
                detail: `${type} panoya kopyalandı`,
                life: 2000,
            });
        }
    };

    const calculatePrice = (months, isTransfer = false) => {
        const { basePrice, discounts, transferDiscount } = pricingConfig;
        const fullPrice = basePrice * months;
        const discount = discounts[months] || 0;
        const discountedPrice = fullPrice * (1 - discount / 100);
        const finalPrice = isTransfer
            ? discountedPrice * (1 - transferDiscount / 100)
            : discountedPrice;
        return Math.floor(finalPrice);
    };

    const calculateSavings = (months) => {
        const fullPrice = pricingConfig.basePrice * months;
        const discountedPrice = calculatePrice(months, true);
        return fullPrice - discountedPrice;
    };

    const handleUserInfoChange = (e) => {
        const { name, value } = e.target;
        setUserInfo(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDistanceSalesApprove = () => {
        setDistanceSalesAgreement(true);
    };

    const handlePlanSelect = (plan) => {
        setSelectedPlan(plan);
        setShowPaymentMethodDialog(true);
    };

    const selectPaymentMethod = (method) => {
        setPaymentMethod(method);
        setDistanceSalesAgreement(false);

        const price = method === 'transfer'
            ? calculatePrice(selectedPlan.months, true)
            : calculatePrice(selectedPlan.months, false);

        setUserInfo(prev => ({
            ...prev,
            paymentMethod: method,
            paymentAmount: price,
            planMonths: selectedPlan.months
        }));

        setShowPaymentMethodDialog(false);
        setShowUserInfoDialog(true);
    };

    const completePayment = async (paymentType) => {
        if (!distanceSalesAgreement) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Mesafeli satış sözleşmesini kabul etmelisiniz.",
                life: 3000,
            });
            return;
        }

        setLoading(true);
        const isPaid = paymentType === 1 ? 0 : 1;
        const paymentData = {
            ...userInfo,
            paymentType,
            isPaid,
            distanceSellingContract: 1
        };

        try {
            const response = await axiosInstance.post('/api/v1/users-payment-request/save', paymentData);
            if (response?.status === 200) {
                toast.current.show({
                    severity: "success",
                    summary: "Başarılı",
                    detail: "Ödeme talebiniz başarıyla kaydedildi.",
                    life: 3000,
                });
                setShowUserInfoDialog(false);
                setShowPaymentConfirmation(true);
            } else {
                toast.current.show({
                    severity: "warning",
                    summary: "Uyarı",
                    detail: "Ödeme talebi kaydedilemedi.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error(error);
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Ödeme talebi kaydedilirken bir hata oluştu.",
                life: 3000,
            });
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const handleCreditCardPayment = () => {
        if (!distanceSalesAgreement) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Mesafeli satış sözleşmesini kabul etmelisiniz.",
                life: 3000,
            });
            return;
        }

        const isPaidSuccess = true;
        if (isPaidSuccess) {
            completePayment(2);
        } else {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Kredi kartı ödemesi başarısız oldu.",
                life: 3000,
            });
        }
    };

    const finalizeProcess = () => {
        toast.current.show({
            severity: "success",
            summary: t('success'),
            detail: t('paymentSuccess'),
            life: 3000,
        });

        setShowPaymentConfirmation(false);
        setSelectedPlan(null);
        setPaymentMethod(null);
        setDistanceSalesAgreement(false);
        setUserInfo({
            name: user ? (user.name && user.surname ? `${user.name} ${user.surname}` : user.name || '') : '',
            phoneNumber: user ? user.phoneNumber1 || '' : '',
            identity: user ? user.identityNumber || '' : '',
            description: '',
            paymentAmount: 0,
            paymentMethod: '',
            planMonths: 0
        });
    };

    // Mobil için kompakt plan kartı
    const planCardTemplate = (plan) => {
        const transferPrice = calculatePrice(plan.months, true);
        const timeDiscount = pricingConfig.discounts[plan.months] || 0;
        const totalSavings = calculateSavings(plan.months);

        return (
            <div className="px-2 pb-4 w-full">
                <div
                    className={`relative bg-white bg-opacity-95 dark:bg-gray-800 rounded-2xl shadow-2xl p-4 cursor-pointer transition-all duration-300 transform hover:scale-105 min-h-[450px] mx-auto max-w-sm
                    ${plan.isPopular ?
                            'border-4 border-orange-500 ring-4 ring-orange-300 dark:ring-orange-600' :
                            'border-2 border-gray-200 dark:border-gray-600'
                        }
                `}
                    onClick={() => handlePlanSelect(plan)}
                >
                    {/* Popular Badge */}
                    {plan.isPopular && (
                        <div className="absolute -top-0 left-1/2 transform -translate-x-1/2 z-10">
                            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full font-bold text-xs shadow-lg animate-pulse whitespace-nowrap">
                                {plan.badge}
                            </div>
                        </div>
                    )}

                    {plan.badge && !plan.isPopular && (
                        <div className="absolute -top-0 left-1/2 transform -translate-x-1/2 z-10">
                            <div className="bg-blue-500 text-white px-2 py-1 rounded-full font-bold text-xs shadow-lg whitespace-nowrap">
                                {plan.badge}
                            </div>
                        </div>
                    )}

                    <div className={`${plan.isPopular ? 'pt-6' : 'pt-4'}`}>
                        {/* Plan Header */}
                        <div className="text-center mb-3">
                            <h2 className={`text-lg font-bold ${plan.isPopular ? 'text-orange-600 dark:text-orange-400' : 'text-logo-darkest dark:text-white'}`}>
                                {plan.label}
                            </h2>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                {plan.savings}
                            </p>
                        </div>

                        {/* Savings Highlight */}
                        {totalSavings > 0 && (
                            <div className={`text-center mb-3 p-2 rounded-lg ${plan.isPopular ? 'bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900 dark:to-orange-900' : 'bg-green-100 dark:bg-green-900'}`}>
                                <div className={`text-sm font-bold ${plan.isPopular ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'}`}>
                                    💰 {totalSavings.toLocaleString('tr-TR')} ₺ Tasarruf!
                                </div>
                            </div>
                        )}

                        {/* Discount Badges */}
                        <div className="flex flex-wrap gap-1 justify-center mb-3">
                            {timeDiscount > 0 && (
                                <div className={`${plan.isPopular ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'} px-2 py-1 rounded-full text-xs font-semibold`}>
                                    %{timeDiscount} İndirim
                                </div>
                            )}
                            <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded-full text-xs font-semibold">
                                Havale +%5
                            </div>
                        </div>

                        {/* Best Price */}
                        <div className={`p-3 rounded-xl border-2 mb-3 ${plan.isPopular ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900 dark:to-yellow-900' : 'border-green-400 bg-green-50 dark:bg-green-900'}`}>
                            <div className={`text-xs text-center mb-1 font-semibold ${plan.isPopular ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'}`}>
                                🏆 En Avantajlı
                            </div>
                            <div className={`text-xl font-bold text-center ${plan.isPopular ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                                {transferPrice.toLocaleString('tr-TR')} ₺
                            </div>
                            <div className="text-xs text-center text-gray-600 dark:text-gray-400">
                                Havale ile
                            </div>
                            {plan.months > 1 && (
                                <div className="text-xs text-center text-gray-500 dark:text-gray-400">
                                    {Math.floor(transferPrice / plan.months).toLocaleString('tr-TR')} ₺ / aylık
                                </div>
                            )}
                        </div>

                        {/* Features - Compressed */}
                        <div className="space-y-1 mb-4 text-xs">
                            <div className="flex items-center">
                                <i className="pi pi-check text-green-500 mr-1 text-xs"></i>
                                <span className="text-gray-700 dark:text-gray-300">Tam Erişim</span>
                            </div>
                            <div className="flex items-center">
                                <i className="pi pi-check text-green-500 mr-1 text-xs"></i>
                                <span className="text-gray-700 dark:text-gray-300">7/24 Destek</span>
                            </div>
                            <div className="flex items-center">
                                <i className="pi pi-check text-green-500 mr-1 text-xs"></i>
                                <span className="text-gray-700 dark:text-gray-300">Ücretsiz Güncellemeler</span>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <Button
                            label={plan.isPopular ? "🚀 Hemen Başla" : "Seç"}
                            className={`w-full p-2 font-bold text-sm transition-all duration-300 ${plan.isPopular
                                ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-xl'
                                : 'bg-logo-dark hover:bg-logo-darkest text-white shadow-lg'
                                }`}
                            onClick={() => handlePlanSelect(plan)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="relative min-h-screen w-full max-w-full overflow-x-hidden">
            <Toast ref={toast} />

            <DistanceSalesModal
                visible={distanceSalesModalVisible}
                onHide={() => setDistanceSalesModalVisible(false)}
                onApprove={handleDistanceSalesApprove}
            />

            <div
                className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 dark:filter dark:grayscale-[70%]"
                style={{
                    backgroundImage: `url(${bgImage})`,
                }}
            />

            {/* Main Content */}
            <div className="relative p-3 md:p-10 flex flex-col min-h-screen max-w-full">
                {/* Hero Section */}
                <div className="text-center mb-6 md:mb-12 bg-white bg-opacity-95 dark:bg-gray-800 rounded-2xl p-4 md:p-8 shadow-2xl max-w-full">
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-logo-darkest dark:text-white mb-3">
                        🚀 {t('pricingPlans')}
                    </h1>
                    {user ? <p className="text-base md:text-2xl text-logo-dark dark:text-gray-300 mb-3 font-bold">
                        Sn. {user?.name} {user?.surname}
                    </p> : null}

                    <p className="text-base md:text-xl text-logo-dark dark:text-gray-300 mb-3">
                        {t('selectPlanSubtitle')}
                    </p>
                    <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-4 py-2 rounded-full">
                        <i className="pi pi-shield-check"></i>
                        <span className="font-bold text-sm md:text-base">{t('taxesIncluded')}</span>
                    </div>

                    {/* Urgency Banner */}
                    <div className="mt-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 md:px-6 py-2 md:py-3 rounded-xl shadow-lg">
                        <div className="flex items-center justify-center gap-2">
                            <i className="pi pi-clock animate-pulse"></i>
                            <span className="font-bold text-sm md:text-base">⚡ Sınırlı Süre! En Uzun Vade En Büyük İndirim!</span>
                        </div>
                    </div>
                </div>

                {/* Pricing Cards - Desktop */}
                <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 max-w-7xl mx-auto w-full">
                    {pricingConfig.plans.map((plan, index) => {
                        const fullPrice = plan.months * pricingConfig.basePrice;
                        const timeDiscountedPrice = calculatePrice(plan.months);
                        const transferPrice = calculatePrice(plan.months, true);
                        const timeDiscount = pricingConfig.discounts[plan.months] || 0;
                        const totalSavings = calculateSavings(plan.months);

                        return (
                            <div
                                key={plan.id}
                                className={`relative bg-white bg-opacity-95 dark:bg-gray-800 rounded-2xl shadow-2xl p-4 cursor-pointer transition-all duration-300 transform hover:scale-105 min-h-[400px]
    ${plan.isPopular ?
                                        'border-4 border-orange-500 ring-4 ring-orange-300 dark:ring-orange-600' :
                                        'border-2 border-gray-200 dark:border-gray-600'
                                    }
`}
                                onClick={() => handlePlanSelect(plan)}
                            >
                                {/* Popular Badge */}
                                {plan.isPopular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                                        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-2 rounded-full font-bold text-sm shadow-lg animate-pulse">
                                            {plan.badge}
                                        </div>
                                    </div>
                                )}

                                {plan.badge && !plan.isPopular && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                                        <div className="bg-blue-500 text-white px-3 py-1 rounded-full font-bold text-xs shadow-lg">
                                            {plan.badge}
                                        </div>
                                    </div>
                                )}

                                <div className={`p-4 md:p-6 ${plan.isPopular ? 'pt-8' : 'pt-6'}`}>
                                    {/* Plan Header */}
                                    <div className="text-center mb-4">
                                        <h2 className={`text-lg md:text-xl font-bold ${plan.isPopular ? 'text-orange-600 dark:text-orange-400' : 'text-logo-darkest dark:text-white'}`}>
                                            {plan.label}
                                        </h2>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {plan.savings}
                                        </p>
                                    </div>

                                    {/* Savings Highlight */}
                                    {totalSavings > 0 && (
                                        <div className={`text-center mb-4 p-3 rounded-lg ${plan.isPopular ? 'bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900 dark:to-orange-900' : 'bg-green-100 dark:bg-green-900'}`}>
                                            <div className={`text-lg font-bold ${plan.isPopular ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'}`}>
                                                💰 {totalSavings.toLocaleString('tr-TR')} ₺ Tasarruf!
                                            </div>
                                        </div>
                                    )}

                                    {/* Discount Badges */}
                                    <div className="flex flex-wrap gap-1 justify-center mb-4">
                                        {timeDiscount > 0 && (
                                            <div className={`${plan.isPopular ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'} px-2 py-1 rounded-full text-xs font-semibold`}>
                                                %{timeDiscount} {t('discount')}
                                            </div>
                                        )}
                                        <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded-full text-xs font-semibold">
                                            Havale +%5
                                        </div>
                                    </div>

                                    {/* Pricing */}
                                    <div className="space-y-3 mb-6">
                                        {/* Original Price */}
                                        {timeDiscount > 0 && (
                                            <div className="text-center">
                                                <div className="text-lg line-through opacity-60 text-gray-500">
                                                    {fullPrice.toLocaleString('tr-TR')} ₺
                                                </div>
                                            </div>
                                        )}

                                        {/* Best Price - Transfer */}
                                        <div className={`p-4 rounded-xl border-2 ${plan.isPopular ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900 dark:to-yellow-900' : 'border-green-400 bg-green-50 dark:bg-green-900'}`}>
                                            <div className={`text-xs text-center mb-1 font-semibold ${plan.isPopular ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'}`}>
                                                🏆 En Avantajlı Fiyat
                                            </div>
                                            <div className={`text-2xl md:text-3xl font-bold text-center ${plan.isPopular ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                                                {transferPrice.toLocaleString('tr-TR')} ₺
                                            </div>
                                            <div className="text-xs text-center text-gray-600 dark:text-gray-400">
                                                Havale ile ödeme
                                            </div>
                                            {plan.months > 1 && (
                                                <div className="text-xs text-center text-gray-500 dark:text-gray-400">
                                                    {Math.floor(transferPrice / plan.months).toLocaleString('tr-TR')} ₺ / aylık
                                                </div>
                                            )}
                                        </div>

                                        {/* Credit Card Price */}
                                        <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-700">
                                            <div className="text-xs text-center mb-1 text-gray-600 dark:text-gray-400">
                                                Kredi kartı ile
                                            </div>
                                            <div className="text-lg font-semibold text-center text-gray-800 dark:text-gray-200">
                                                {timeDiscountedPrice.toLocaleString('tr-TR')} ₺
                                            </div>
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <div className="space-y-2 mb-6">
                                        <div className="flex items-center text-sm">
                                            <i className="pi pi-check text-green-500 mr-2"></i>
                                            <span className="text-gray-700 dark:text-gray-300">{t('noSurprise')}</span>
                                        </div>
                                        <div className="flex items-center text-sm">
                                            <i className="pi pi-check text-green-500 mr-2"></i>
                                            <span className="text-gray-700 dark:text-gray-300">{t('fullAccess')}</span>
                                        </div>
                                        <div className="flex items-center text-sm">
                                            <i className="pi pi-check text-green-500 mr-2"></i>
                                            <span className="text-gray-700 dark:text-gray-300">{t('dedicatedSupport')}</span>
                                        </div>
                                        <div className="flex items-center text-sm">
                                            <i className="pi pi-check text-green-500 mr-2"></i>
                                            <span className="text-gray-700 dark:text-gray-300">{t('regularUpdates')}</span>
                                        </div>
                                    </div>

                                    {/* CTA Button */}
                                    <Button
                                        label={plan.isPopular ? "🚀 Hemen Başla" : t('selectPlan')}
                                        className={`w-full p-3 font-bold transition-all duration-300 ${plan.isPopular
                                            ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-xl transform hover:scale-105'
                                            : 'bg-logo-dark hover:bg-logo-darkest text-white shadow-lg hover:shadow-xl'
                                            }`}
                                        onClick={() => handlePlanSelect(plan)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Pricing Cards - Mobile Carousel */}
                <div className="md:hidden mb-10 w-full overflow-hidden">
                    <div className="w-full max-w-full">
                        <Carousel
                            value={pricingConfig.plans}
                            itemTemplate={planCardTemplate}
                            numVisible={1}
                            numScroll={1}
                            circular={true}
                            autoplayInterval={3000}
                            showNavigators={true}
                            showIndicators={true}
                            prevIcon="pi pi-chevron-left"
                            nextIcon="pi pi-chevron-right"
                            className="pricing-carousel w-full"
                            contentClassName="carousel-content"
                        />
                    </div>
                </div>

                {/* Trust Signals */}
                <div className="bg-white bg-opacity-90 dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-xl max-w-4xl mx-auto w-full mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 text-center">
                        <div className="flex flex-col items-center">
                            <i className="pi pi-shield text-2xl md:text-3xl text-green-500 mb-2"></i>
                            <h3 className="font-bold text-logo-darkest dark:text-white mb-1 text-sm md:text-base">Güvenli Ödeme</h3>
                            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">256-bit SSL ile korumalı</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <i className="pi pi-clock text-2xl md:text-3xl text-blue-500 mb-2"></i>
                            <h3 className="font-bold text-logo-darkest dark:text-white mb-1 text-sm md:text-base">Anında Aktivasyon</h3>
                            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Ödeme sonrası hızlı başlangıç</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <i className="pi pi-users text-2xl md:text-3xl text-purple-500 mb-2"></i>
                            <h3 className="font-bold text-logo-darkest dark:text-white mb-1 text-sm md:text-base">7/24 Destek</h3>
                            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Kesintisiz müşteri hizmeti</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Method Selection Modal */}
            <Dialog
                visible={showPaymentMethodDialog}
                onHide={() => setShowPaymentMethodDialog(false)}
                header={`💳 ${selectedPlan?.label} - Ödeme Yöntemi Seçin`}
                style={{ width: '95%', maxWidth: '600px' }}
                modal
                className="p-fluid"
            >
                {selectedPlan && (
                    <div className="space-y-6">
                        {/* Plan Summary */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl text-center">
                            <h3 className="text-xl font-bold text-logo-darkest dark:text-white mb-2">
                                {selectedPlan.label}
                            </h3>
                            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                                {calculatePrice(selectedPlan.months, true).toLocaleString('tr-TR')} ₺
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                En avantajlı fiyat (Havale ile)
                            </div>
                        </div>

                        {/* Payment Options */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Bank Transfer */}
                            <div
                                className="border-2 border-green-500 rounded-xl p-6 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900 transition-all duration-300 transform hover:scale-105"
                                onClick={() => selectPaymentMethod('transfer')}
                            >
                                <div className="text-center">
                                    <i className="pi pi-wallet text-4xl text-green-500 mb-3"></i>
                                    <h4 className="font-bold text-green-700 dark:text-green-400 mb-2">
                                        🏆 Havale / EFT
                                    </h4>
                                    <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-2">
                                        {calculatePrice(selectedPlan.months, true).toLocaleString('tr-TR')} ₺
                                    </div>
                                    <div className="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                                        %{pricingConfig.transferDiscount} İndirimli
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        En uygun fiyat garantisi
                                    </p>
                                </div>
                            </div>

                            {/* Credit Card */}
                            <div
                                className="border-2 border-blue-500 rounded-xl p-6 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900 transition-all duration-300 transform hover:scale-105"
                                onClick={() => selectPaymentMethod('creditCard')}
                            >
                                <div className="text-center">
                                    <i className="pi pi-credit-card text-4xl text-blue-500 mb-3"></i>
                                    <h4 className="font-bold text-blue-700 dark:text-blue-400 mb-2">
                                        💳 Kredi Kartı
                                    </h4>
                                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                                        {calculatePrice(selectedPlan.months).toLocaleString('tr-TR')} ₺
                                    </div>
                                    <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                                        Anında Aktivasyon
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Hızlı ve güvenli ödeme
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="text-center">
                            <Button
                                label="❌ İptal"
                                className="border border-gray-400 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 px-6 py-2"
                                onClick={() => setShowPaymentMethodDialog(false)}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* User Info Dialog */}
            <Dialog
                visible={showUserInfoDialog}
                onHide={() => setShowUserInfoDialog(false)}
                header={`👤 ${t('userInformation')}`}
                style={{ width: '95%', maxWidth: '500px' }}
                modal
                className="p-fluid"
                footer={
                    <div className="mt-2 flex flex-col md:flex-row justify-between gap-3">
                        <Button
                            label="İptal"
                            icon="pi pi-times"
                            className="border border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 px-4 py-2"
                            onClick={() => {
                                setShowUserInfoDialog(false);
                                setDistanceSalesAgreement(false);
                            }}
                        />
                        <Button
                            label={paymentMethod === 'transfer' ? 'Havale Talebini Gönder' : 'Ödemeyi Tamamla'}
                            icon={paymentMethod === 'transfer' ? "pi pi-check" : "pi pi-credit-card"}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 font-bold"
                            onClick={() => paymentMethod === 'transfer' ? completePayment(1) : handleCreditCardPayment()}
                            disabled={
                                !userInfo.name ||
                                !userInfo.phoneNumber ||
                                !userInfo.identity ||
                                !distanceSalesAgreement
                            }
                        />
                    </div>
                }
            >
                <div className="space-y-6">
                    {/* Payment Summary */}
                    <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 p-4 rounded-xl">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">📋 {t('paymentSummary')}</h3>
                            <div className="text-right">
                                <div className="font-bold">{selectedPlan?.label}</div>
                                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    {userInfo.paymentAmount.toLocaleString('tr-TR')} ₺
                                </div>
                            </div>
                        </div>

                        {paymentMethod === 'transfer' && (
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg mt-4">
                                <h3 className="font-semibold mb-3 text-center">
                                    🏦 {t('bankAccounts')}
                                </h3>
                                <div className="space-y-3 max-h-48 overflow-y-auto">
                                    {bankAccounts.map((account, index) => (
                                        <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <div className="font-semibold text-logo-darkest dark:text-white mb-2">{account.bank}</div>

                                            {/* Hesap İsmi ile Kopyala Butonu */}
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1">
                                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                                        <strong>Hesap İsmi:</strong>
                                                    </div>
                                                    <div className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                                                        {account.name}
                                                    </div>
                                                </div>
                                                <Button
                                                    icon="pi pi-copy"
                                                    className="p-button-text p-button-sm ml-2 hover:bg-gray-200 dark:hover:bg-gray-600"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(account.name, 'Hesap İsmi');
                                                    }}
                                                    tooltip="Hesap İsmini Kopyala"
                                                    tooltipOptions={{ position: 'top' }}
                                                />
                                            </div>

                                            {/* IBAN ile Kopyala Butonu */}
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                                        <strong>IBAN:</strong>
                                                    </div>
                                                    <div className="text-sm text-gray-800 dark:text-gray-200 font-mono font-medium break-all">
                                                        {account.iban}
                                                    </div>
                                                </div>
                                                <Button
                                                    icon="pi pi-copy"
                                                    className="p-button-text p-button-sm ml-2 hover:bg-gray-200 dark:hover:bg-gray-600"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(account.iban, 'IBAN');
                                                    }}
                                                    tooltip="IBAN'ı Kopyala"
                                                    tooltipOptions={{ position: 'top' }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Warning */}
                    <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 p-4 rounded-lg">
                        <div className="flex items-start">
                            <i className="pi pi-exclamation-triangle text-red-500 text-xl mr-3 mt-1"></i>
                            <div>
                                <h4 className="font-bold text-red-700 dark:text-red-300 mb-2">Önemli Uyarı</h4>
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    Lütfen aşağıdaki bilgileri eksiksiz ve doğru doldurunuz!
                                    İşlem sonrası teyit amaçlı aranabilirsiniz.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-6">
                        <InputText
                            label="👤 İsim - Soyisim"
                            name="name"
                            value={userInfo.name}
                            onChange={handleUserInfoChange}
                            clearFunction={() => setUserInfo(prev => ({ ...prev, name: '' }))}
                            required
                        />
                        <InputText
                            label="📱 Telefon Numarası"
                            name="phoneNumber"
                            type="phone"
                            value={userInfo.phoneNumber}
                            onChange={handleUserInfoChange}
                            clearFunction={() => setUserInfo(prev => ({ ...prev, phoneNumber: '' }))}
                            required
                        />
                        <InputText
                            label="🆔 T.C Kimlik No"
                            name="identity"
                            value={userInfo.identity}
                            onChange={handleUserInfoChange}
                            clearFunction={() => setUserInfo(prev => ({ ...prev, identity: '' }))}
                            required
                        />
                        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 p-4 rounded-lg">
                            <div className="flex items-start">
                                <i className="pi pi-exclamation-triangle text-red-500 text-xl mr-3 mt-1"></i>
                                <div>
                                    <h4 className="font-bold text-red-700 dark:text-red-300 mb-2">Önemli Uyarı</h4>
                                    <p className="text-sm text-red-600 dark:text-red-400">
                                        Farklı bir hesaptan para gönderdiyseniz veya özel bir durumunuz varsa açıklama alanını doldurunuz. ***
                                    </p>
                                </div>
                            </div>
                        </div>
                        <InputText
                            type="textarea"
                            label="📝 Açıklama (İsteğe Bağlı)"
                            name="description"
                            value={userInfo.description}
                            onChange={handleUserInfoChange}
                            clearFunction={() => setUserInfo(prev => ({ ...prev, description: '' }))}
                        />
                    </div>

                    {/* Agreement */}
                    <div className="bg-orange-50 dark:bg-orange-900 border border-orange-200 dark:border-orange-700 p-4 rounded-lg">
                        <div className="flex items-start">
                            <Checkbox
                                inputId="distanceSalesAgreement"
                                checked={distanceSalesAgreement}
                                onChange={e => setDistanceSalesAgreement(e.checked)}
                                className="mr-3 mt-1"
                                disabled={!distanceSalesAgreement}
                            />
                            <div className="flex-1">
                                <label htmlFor="distanceSalesAgreement" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                                    <button
                                        type="button"
                                        className="text-orange-600 dark:text-orange-400 font-semibold underline hover:text-orange-800 dark:hover:text-orange-300"
                                        onClick={() => setDistanceSalesModalVisible(true)}
                                    >
                                        Mesafeli Satış Sözleşmesi
                                    </button>
                                    'ni okudum ve kabul ediyorum.
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                {distanceSalesAgreement && (
                                    <div className="flex items-center mt-2 text-green-600 dark:text-green-400">
                                        <i className="pi pi-check-circle mr-2"></i>
                                        <span className="text-sm">Sözleşme kabul edildi.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Payment Confirmation Dialog */}
            <Dialog
                visible={showPaymentConfirmation}
                onHide={() => setShowPaymentConfirmation(false)}
                header="🎉 Ödeme Onayı"
                style={{ width: '95%', maxWidth: '500px' }}
                modal
                className="p-fluid"
                footer={
                    <div className="flex justify-center mt-4">
                        <Button
                            label="✅ Devam Et"
                            icon="pi pi-check"
                            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 font-bold rounded-xl"
                            onClick={finalizeProcess}
                        />
                    </div>
                }
            >
                {paymentMethod === 'transfer' ? (
                    <div className="text-center space-y-4">
                        <i className="pi pi-check-circle text-green-500 text-6xl"></i>
                        <h3 className="text-2xl font-bold text-green-600"> Havale Talebi Alındı!</h3>
                        <p className="text-gray-600 dark:text-gray-300">
                            Havale işleminizi tamamladıktan sonra hesabınız otomatik olarak aktifleştirilecektir.
                        </p>
                        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                            <h4 className="font-semibold mb-3">📊 Ödeme Bilgileri</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>💰 Tutar:</span>
                                    <span className="font-bold">{userInfo.paymentAmount.toLocaleString('tr-TR')} ₺</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>📅 Plan:</span>
                                    <span className="font-bold">{selectedPlan?.label}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center space-y-4">
                        <i className="pi pi-check-circle text-green-500 text-6xl"></i>
                        <h3 className="text-2xl font-bold text-green-600">🎉 Ödeme Başarılı!</h3>
                        <p className="text-gray-600 dark:text-gray-300">
                            Kredi kartı ödemeniz başarıyla tamamlandı. Hesabınız aktifleştirildi.
                        </p>
                        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                            <h4 className="font-semibold mb-3">📊 Ödeme Bilgileri</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>💰 Tutar:</span>
                                    <span className="font-bold">{userInfo.paymentAmount.toLocaleString('tr-TR')} ₺</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>📅 Plan:</span>
                                    <span className="font-bold">{selectedPlan?.label}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            <style jsx global>{`
    .pricing-carousel {
        padding-top: 20px;
        width: 100% !important;
        max-width: 100% !important;
    }
    
    .pricing-carousel .p-carousel-content {
        overflow: visible !important;
        width: 100% !important;
    }
    
    .pricing-carousel .p-carousel-container {
        overflow: visible !important;
        width: 100% !important;
    }
    
    .pricing-carousel .p-carousel-items-container {
        overflow: visible !important;
        width: 100% !important;
    }
    
    .pricing-carousel .p-carousel-item {
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden;
    }
    
    .pricing-carousel .p-carousel-indicators {
        margin-top: 1.5rem;
    }
    
    .pricing-carousel .p-carousel-indicator {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: #e5e7eb;
        margin: 0 4px;
        transition: all 0.3s ease;
    }
    
    .pricing-carousel .p-carousel-indicator.p-highlight {
        background-color: #3b82f6;
        width: 24px;
        border-radius: 10px;
    }
    
    .pricing-carousel .p-carousel-prev,
    .pricing-carousel .p-carousel-next {
        color: #4f46e5;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 50%;
        width: 36px;
        height: 36px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .pricing-carousel .p-carousel-prev:hover,
    .pricing-carousel .p-carousel-next:hover {
        background: rgba(255, 255, 255, 1);
        color: #4338ca;
    }

    /* Copy button hover effects */
    .p-button-text:hover {
        background-color: rgba(156, 163, 175, 0.2) !important;
        color: #4f46e5 !important;
    }

    .dark .p-button-text:hover {
        background-color: rgba(75, 85, 99, 0.3) !important;
        color: #8b5cf6 !important;
    }
    
    @media (max-width: 768px) {
        .pricing-carousel .p-carousel-item {
            padding: 0 !important;
            width: 100% !important;
        }
        
        /* Mobilde yatay kaydırmayı engelle */
        body {
            overflow-x: hidden;
        }
    }
`}</style>
        </div>
    );
};

export default Pricing;